import styled from '@emotion/styled'
import React, { useEffect } from 'react'
import { Platform, getPlatformDisplayName } from '../../../../firebase/firestore/Platform'
import fitty from 'fitty'

type Props = {
  targetId: string
  backgroundColor: string | 'image'
  fontColor: string
  fontFamily: string
  name: string
  id: string
  platform: Array<Platform>
  playTimeWeekDayBegin: number
  playTimeWeekDayEnd: number
  playTimeWeekEndBegin: number
  playTimeWeekEndEnd: number
  playTimeFreeText: string
  playStyle: string
  hitokoto: string
  titleJa: string
  coverImageUrl?: string
}

export const EditorV2Card: React.FC<Props> = ({
  targetId,
  backgroundColor,
  fontColor,
  fontFamily,
  name,
  id,
  platform,
  playTimeWeekDayBegin,
  playTimeWeekDayEnd,
  playTimeWeekEndBegin,
  playTimeWeekEndEnd,
  playTimeFreeText,
  playStyle,
  hitokoto,
  titleJa,
  coverImageUrl,
}) => {
  useEffect(() => {
    fitty('#card-name', {
      maxSize: 24,
      minSize: 12,
      multiLine: true,
    })
    fitty('#card-id', {
      maxSize: 16,
      minSize: 10,
      multiLine: true,
    })
    fitty('#card-platform', {
      maxSize: 12,
      minSize: 10,
      multiLine: true,
    })
    fitty('#card-playstyle', {
      maxSize: 16,
      minSize: 10,
      multiLine: true,
    })
    fitty('#card-hitokoto', {
      maxSize: 16,
      minSize: 10,
      multiLine: true,
    })
    fitty('#card-title', {
      maxSize: 16,
      minSize: 10,
      multiLine: true,
    })
    fitty('#card-playtime-free', {
      maxSize: 16,
      minSize: 10,
      multiLine: true,
    })
    fitty('#card-playtime-weekday', {
      maxSize: 16,
      minSize: 10,
      multiLine: true,
    })
    fitty('#card-playtime-weekend', {
      maxSize: 16,
      minSize: 10,
      multiLine: true,
    })
  }, [name, id, platform, playTimeWeekDayBegin, playTimeWeekDayEnd, playTimeWeekEndBegin, playTimeWeekEndEnd, playTimeFreeText, playStyle, hitokoto, titleJa])


  const bgStyle = backgroundColor === 'image'
    ? {
        backgroundImage: coverImageUrl
          ? `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4)), url(${coverImageUrl})`
          : 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4))',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#212121' // coverImageUrlがない場合のフォールバック背景色
      }
    : {
        backgroundColor: backgroundColor || '#F44336'
      }

  return (
    <CardContainer>
      <CardInner id={targetId} fontColor={fontColor} fontFamily={fontFamily} style={bgStyle}>
        <CardContent>
          <LeftColumn>
            <CardHeader>
            <CoverImageContainer>
              {coverImageUrl ? (
                <CoverImage src={coverImageUrl} alt={titleJa ?? ''} />
              ) : (
                <CoverImagePlaceholder />
              )}
            </CoverImageContainer>
            <CardTitle>
              <span id="card-title">
                {titleJa}
                <br />
                自己紹介カード
              </span>
            </CardTitle>
          </CardHeader>
            <FieldGroup>
              <FieldLabel>なまえ</FieldLabel>
              <FieldValue>
                <span id="card-name">{name}</span>
              </FieldValue>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>ID</FieldLabel>
              <FieldValue>
                <span id="card-id">{id}</span>
              </FieldValue>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>プラットフォーム</FieldLabel>
              <FieldValue>
                <PlatformText id="card-platform">
                  {platform.length > 0
                    ? platform.map((p, index, arr) => {
                        const halfLength = Math.ceil(arr.length / 2);
                        const displayName = getPlatformDisplayName(p);
                        if (index === halfLength - 1 && arr.length > 1) {
                          return displayName + (index < arr.length - 1 ? ' / \n' : '\n');
                        }
                        return index < arr.length - 1 ? displayName + ' / ' : displayName;
                      }).join('')
                    : ''}
                </PlatformText>
              </FieldValue>
            </FieldGroup>
          </LeftColumn>

          <RightColumn>
            <FieldGroup>
              <FieldLabel>プレイ時間</FieldLabel>
              <FieldValue>
                {playTimeFreeText ? (
                  <PlayTimeText>
                    <span id="card-playtime-free">{playTimeFreeText}</span>
                  </PlayTimeText>
                ) : (
                  <PlayTimeText>
                    <span id="card-playtime-weekday">{`平日 ${playTimeWeekDayBegin.toString()}時〜${playTimeWeekDayEnd.toString()}時`}</span>
                    <span id="card-playtime-weekend">{`休日 ${playTimeWeekEndBegin.toString()}時〜${playTimeWeekEndEnd.toString()}時`}</span>
                  </PlayTimeText>
                )}
              </FieldValue>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>プレイスタイル</FieldLabel>
              <FieldValue>
                <MultiLineText id="card-playstyle">{playStyle}</MultiLineText>
              </FieldValue>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>ひとこと</FieldLabel>
              <FieldValue>
                <MultiLineText id="card-hitokoto">{hitokoto}</MultiLineText>
              </FieldValue>
            </FieldGroup>
          </RightColumn>
        </CardContent>
        <GameeeLogo>
          <img src="/static/images/logos/gamee.svg" alt="gamee" width="30" height="10" />
        </GameeeLogo>
      </CardInner>
    </CardContainer>
  )
}

const CardContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  `
  
  const CardInner = styled.div<{ fontColor: string; fontFamily: string }>`
    position: relative;
    width: 100%;
    max-width: 800px;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background-size: cover;
    background-position: center;
    padding: 10px;
    display: flex;
    flex-direction: column;
  
    * {
      color: ${({ fontColor }) => fontColor};
      font-family: ${({ fontFamily }) => `'${fontFamily}', sans-serif`};
    }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  min-height: 60px;
  padding: 5px 0;
  gap: 10px;
`

const CoverImageContainer = styled.div`
  min-width: 40px;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
`

const CoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
`

const CoverImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
`

const CardTitle = styled.div`
  font-size: 1em;
  font-weight: bold;
  line-height: 1.2;
  overflow: hidden;
  width: 100%;
  display: flex;
  align-items: center;
  
  span {
    width: 100%;
    word-break: break-word;
    white-space: pre-wrap;
    hyphens: auto;
  }
`

const GameeeLogo = styled.div`
  position: absolute;
  bottom: 5px;
  right: 5px;
  width: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CardContent = styled.div`
  display: flex;
  gap: 4%;
  height: 100%;
  overflow: hidden;
  width: 100%;
  justify-content: space-between;
`

const LeftColumn = styled.div`
  width: 48%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 2%;
  overflow: hidden;
`

const RightColumn = styled.div`
  width: 48%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 2%;
  overflow: hidden;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2%;
  flex: 1;
`

const FieldLabel = styled.div`
  font-size: 0.35em;
  font-weight: bold;
  margin-bottom: 2%;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.03em;
`

const FieldValue = styled.div`
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 10px;
  padding-top: 1%;
  font-weight: bold;
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  span {
    width: 100%;
    overflow: hidden;
    text-align: center;
  }
`

const PlatformText = styled.span`
  width: 100%;
  white-space: pre-wrap;
  word-break: break-word;
  -webkit-box-orient: vertical;
  max-height: 2.4em;
  line-height: 1.2;
`

const MultiLineText = styled.span`
  width: 100%;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 4.8em;
  line-height: 1.2;
`

const PlayTimeText = styled.div`
  width: 100%;
`